/**
 * CARLUMO — Email signup freebie ("Mystery" gift product)
 * ---------------------------------------------------------
 * The "FREEBIE" Buy X Get Y discount makes the unlisted "Mystery" product
 * free, but a Shopify discount never *adds* a product — it only reprices what
 * is already in the cart. So this script adds Mystery whenever the customer
 * claims the gift.
 *
 * Because the code is now a single known value (window.FreebieGift.code),
 * the logic is deterministic: on the FREEBIE code we add Mystery, then apply
 * the code. Other discount codes are left entirely to the theme.
 *
 * Triggers:
 *   1. Cart discount box — customer types FREEBIE and clicks Apply.
 *   2. URL: /cart?gift_code=FREEBIE  (e.g. a Klaviyo "claim" link).
 *   3. Page load — if FREEBIE is already applied but Mystery is missing.
 *
 * NOTE: UX only / bypassable. Real gating (first order, must be subscribed)
 * lives in the Shopify discount's customer-segment + once-per-customer rules.
 */
(function () {
  'use strict';

  var cfg = window.FreebieGift || {};
  if (!cfg.variantId) return;

  var VARIANT = String(cfg.variantId);
  var CODE = cfg.code || '';
  var SS_KEY = 'carlumo_freebie_done';
  var busy = false;

  function log() {
    if (cfg.debug && window.console) console.log.apply(console, ['[freebie]'].concat([].slice.call(arguments)));
  }
  function lc(s) { return String(s).toLowerCase(); }
  function isGiftCode(code) { return CODE && lc(code) === lc(CODE); }

  // ---- routes / fetch -----------------------------------------------------

  function root() {
    return (window.theme && theme.routes && theme.routes.root_url) ||
      (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }
  function route(name, fallback) {
    return (window.theme && theme.routes && theme.routes[name]) || (root().replace(/\/$/, '') + fallback);
  }
  function postConfig() {
    if (window.theme && theme.utils && theme.utils.fetchConfig) return theme.utils.fetchConfig('json');
    return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
  }

  function getCart() {
    return fetch(route('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }
  function cartHasMystery(cart) {
    return (cart.items || []).some(function (i) { return String(i.variant_id) === VARIANT; });
  }
  function addMystery() {
    var c = postConfig();
    c.body = JSON.stringify({ items: [{ id: Number(cfg.variantId), quantity: 1 }] });
    return fetch(route('cart_add_url', '/cart/add'), c).then(function (r) { return r.json(); });
  }
  function removeMystery() {
    var c = postConfig();
    c.body = JSON.stringify({ id: VARIANT, quantity: 0 });
    return fetch(route('cart_change_url', '/cart/change'), c).then(function (r) { return r.json(); });
  }
  function setDiscounts(codes) {
    var c = postConfig();
    c.body = JSON.stringify({ discount: codes.join(',') });
    return fetch(route('cart_update_url', '/cart/update'), c).then(function (r) { return r.json(); });
  }
  function isApplied(cart, code) {
    return (cart.discount_codes || []).some(function (d) { return lc(d.code) === lc(code) && d.applicable === true; });
  }
  function codeEntered(cart, code) {
    return (cart.discount_codes || []).some(function (d) { return lc(d.code) === lc(code); });
  }

  function eligible() {
    // Best-effort: skip logged-in customers with previous orders. Guests pass
    // (the Shopify discount's segment + once-per-customer rules gate them).
    return cfg.customerHasOrdered !== true;
  }

  function refreshCartUI(cart, open) {
    if (window.theme && theme.pubsub) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'freebie-gift', cart: cart });
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: !!open } }));
  }

  // ---- core: add Mystery, then apply the gift code ------------------------

  // Build a clean cart URL (drops gift_code, keeps the theme preview if any).
  function cartReloadUrl() {
    var dest = new URL(route('cart_url', '/cart'), window.location.origin);
    var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) dest.searchParams.set('preview_theme_id', preview);
    return dest.toString();
  }

  function claimGift(form, input, btn, opts) {
    opts = opts || {};
    if (busy) { log('busy — ignoring'); return; }
    busy = true;
    if (btn) btn.setAttribute('aria-busy', 'true');
    log('claimGift START');

    var addedMystery = false;
    var done = function () {
      if (opts.reload) { window.location.replace(cartReloadUrl()); return; }
      // Non-cart page: drawer is already opening — just strip the gift_code
      // from the address bar so a manual refresh doesn't re-trigger.
      if (opts.fromUrl) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.delete(cfg.urlParam || 'gift_code');
          window.history.replaceState({}, '', u.toString());
        } catch (e) {}
      }
    };

    getCart()
      .then(function (cart) {
        // Only client-side gate: skip logged-in customers with prior orders.
        if (!eligible()) { log('not eligible (repeat buyer)'); showError(form, input); done(); return; }

        var existing = (cart.discount_codes || []).map(function (d) { return d.code; });
        var without = existing.filter(function (c) { return !isGiftCode(c); });
        var target = without.concat(CODE);

        var addStep;
        if (cartHasMystery(cart)) { addStep = Promise.resolve(); }
        else { addStep = addMystery().then(function (r) { addedMystery = true; log('Mystery added:', r && r.item_count != null ? 'ok' : r); }); }

        return addStep
          .then(function () { return setDiscounts(target); })
          .then(function (state) {
            // Mystery is £0, so keep it and the code regardless of whether the
            // BXGY is "applicable" yet — it activates at checkout once the
            // customer has a qualifying item. Shopify still enforces the
            // segment / once-per-customer rules at checkout.
            log('FREEBIE applicable now?', isApplied(state, CODE), '(left applied either way)');
            if (input) input.value = '';
            try { sessionStorage.setItem(SS_KEY, '1'); } catch (e) {}
            return getCart().then(function (c) { refreshCartUI(c, true); done(); });
          });
      })
      .catch(function (e) { if (window.console) console.error('[freebie] claim failed:', e); showError(form, input); })
      .finally(function () { busy = false; if (btn) btn.removeAttribute('aria-busy'); });
  }

  function showError(form, input) {
    if (input) input.value = '';
    var msg = (window.theme && theme.discountStrings && theme.discountStrings.error) ||
      'Discount code cannot be applied to your cart';
    if (form && typeof form.setDiscountError === 'function') { form.setDiscountError(msg); return; }
    if (form && form.lastElementChild) {
      try { form.lastElementChild.lastElementChild.textContent = msg; form.lastElementChild.hidden = false; } catch (e) {}
    }
  }

  // ---- trigger 1: cart discount box --------------------------------------

  function discountForm(target) {
    if (!target || !target.closest) return null;
    var btn = target.closest('form[is="cart-discount"] [type="submit"]');
    return btn ? btn.closest('form[is="cart-discount"]') : null;
  }

  document.addEventListener('click', function (e) {
    var form = discountForm(e.target);
    if (!form) return;
    var input = form.querySelector('[name="discount"]');
    var code = input ? input.value.trim() : '';
    if (!code) return;                       // empty — let the theme handle it
    if (CODE && !isGiftCode(code)) {         // a different promo — don't interfere
      log('non-gift code "' + code + '" — leaving to theme');
      return;
    }
    log('FREEBIE apply intercepted');
    e.preventDefault();
    e.stopImmediatePropagation();            // block theme handler + native submit
    claimGift(form, input, e.target.closest('[type="submit"]'));
  }, true);

  document.addEventListener('submit', function (e) {
    if (e.target && e.target.matches && e.target.matches('form[is="cart-discount"]')) {
      var input = e.target.querySelector('[name="discount"]');
      var code = input ? input.value.trim() : '';
      if (code && (!CODE || isGiftCode(code))) { e.preventDefault(); e.stopImmediatePropagation(); }
    }
  }, true);

  // ---- triggers 2 & 3: URL param / already-applied on load ----------------

  function maybeAutoClaim() {
    if (!CODE) return;
    var urlAsks = false;
    try {
      var qs = new URLSearchParams(window.location.search);
      var p = qs.get(cfg.urlParam || 'gift_code');
      urlAsks = !!(p && isGiftCode(p));
    } catch (e) {}

    // On the cart PAGE we must reload to re-render; elsewhere just open the drawer.
    var onCartPage = document.body.classList.contains('template-cart') ||
      /(^|\/)cart(\/|$)/.test(window.location.pathname);

    if (urlAsks) { log('URL gift_code present (cart page:', onCartPage, ')'); return claimGift(null, null, null, { reload: onCartPage, fromUrl: true }); }

    // FREEBIE already on the cart (e.g. via /discount/FREEBIE) but no Mystery.
    getCart().then(function (cart) {
      if (codeEntered(cart, CODE) && !cartHasMystery(cart) && eligible()) {
        log('FREEBIE already applied, Mystery missing — adding');
        claimGift(null, null, null, { reload: onCartPage });
      }
    }).catch(function () {});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', maybeAutoClaim);
  else maybeAutoClaim();

  // ---- "only the Mystery gift in the basket" guard ------------------------
  // Toggles html.freebie-gift-only; the snippet's CSS then hides the price,
  // greys the checkout button, hides accelerated buttons and shows the notice.

  function refreshGiftOnly() {
    getCart().then(function (cart) {
      var others = (cart.items || []).filter(function (i) { return String(i.variant_id) !== VARIANT; });
      var giftOnly = (cart.item_count > 0) && cartHasMystery(cart) && others.length === 0;
      document.documentElement.classList.toggle('freebie-gift-only', giftOnly);
      log('giftOnly:', giftOnly);
    }).catch(function () {});
  }

  // Belt-and-braces: block the checkout button click while gift-only.
  document.addEventListener('click', function (e) {
    if (!document.documentElement.classList.contains('freebie-gift-only')) return;
    if (e.target && e.target.closest && e.target.closest('[name="checkout"]')) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  if (window.theme && theme.pubsub) {
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function () { refreshGiftOnly(); });
  }
  document.addEventListener('cart:refresh', function () { setTimeout(refreshGiftOnly, 200); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshGiftOnly);
  else refreshGiftOnly();

  log('initialised', cfg);
})();
